import * as React from "react";
import type { SVGProps } from "react";

import { Box } from "@mui/material";

// The product scale used by the dashboard's SpiceLevelSelector.
export const MAX_SPICE_LEVEL = 4;

export const SPICE_LEVEL_LABELS = {
  1: "Mild",
  2: "Slightly hot",
  3: "Very hot",
  4: "Extra hot",
} as const;

export type SpiceLevel = keyof typeof SPICE_LEVEL_LABELS;

/**
 * Spice data comes from persisted dishes, so protect guest-facing UI from
 * legacy or malformed values instead of treating every truthy number as heat.
 */
export const isSpiceLevel = (level: unknown): level is SpiceLevel =>
  typeof level === "number" &&
  Number.isInteger(level) &&
  level >= 1 &&
  level <= MAX_SPICE_LEVEL;

export const spiceLevelAriaLabel = (level: SpiceLevel): string =>
  `Spice level: ${SPICE_LEVEL_LABELS[level]} (${level} of ${MAX_SPICE_LEVEL})`;

const ChilliGlyph = (props: SVGProps<SVGSVGElement>) => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    {...props}
  >
    <path
      d="M12.52 22.07A21.8 21.8 0 0 0 20.68 14a4.32 4.32 0 0 0 -0.83 -5.48h0a4.33 4.33 0 0 0 -6.58 1l-3.4 5.68a8.71 8.71 0 0 1 -7.45 4.22h-0.83a0.71 0.71 0 0 0 -0.54 1.18h0a9.94 9.94 0 0 0 11.47 1.47Z"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16.32 7.45c0.86 -2.4 3.35 -3.49 5 -2.45 1.47 0.91 1.77 3.06 0.69 4.82l-0.77 1.39"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="m21.07 4.85 2.06 -4.1"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Repeated chilli glyphs for a verified, four-level dish heat value.
 *
 * The glyphs are decorative. Their wrapper provides the sole accessible name
 * so a screen reader announces one useful description rather than every icon.
 */
export const SpiceLevelIcon = ({
  level,
}: {
  level: number | null | undefined;
}) => {
  if (!isSpiceLevel(level)) return null;

  return (
    <Box
      component="span"
      role="img"
      aria-label={spiceLevelAriaLabel(level)}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "1px",
        flexShrink: 0,
        fontSize: "14px",
        color: (theme) => theme.palette.oscar.light.content.danger,
      }}
    >
      {Array.from({ length: level }).map((_, index) => (
        <ChilliGlyph key={index} />
      ))}
    </Box>
  );
};
